import { MerchantsComponent } from './../merchants/merchants.component';
import { Component, OnInit, Injector, Input } from '@angular/core';
import { CustomerAccount } from '../../data/CustomerAccount';
import { UntypedFormControl, UntypedFormGroup, NgForm, Validators } from '@angular/forms';
import * as customValidator from '../../validators/merchant-validator';
import { Subscription } from 'rxjs';

export interface AdjustmentReason {
  COLUMN_VALUE: string;
  DESCR: string;
}

@Component({
  selector: 'app-merchant-adjustments',
  templateUrl: './merchant-adjustments.component.html',
  styleUrls: ['./merchant-adjustments.component.css']
})
export class MerchantAdjustmentsComponent extends MerchantsComponent
  implements OnInit {
  constructor(injector: Injector) {
    super(injector);
  }
  filterTrans = [];
  merchantAccountInfoSub: Subscription;
  dataLoading = false;
  srcAccts: CustomerAccount[];
  adjustAmt: number;
  adjustReason: string;
  srcAcct: number;
  desAcct: number;
  xferAmt: number;
  transferSummary: any = null;
  adjustmentReasons: AdjustmentReason[];
  financialAdjustmentsForm: UntypedFormGroup;
  balanceTransferForm: UntypedFormGroup;
  merchantCardLimitsForm: UntypedFormGroup;
  merchantTransSub: Subscription = null;
  stripAccount: number;
  transactions: any[] = [];
  accountType: string;
  applyBalanceButtonSpinnerString = '';
  updateLimitButtonSpinnerString = '';
  cardLimit: string;

  _endDate = new Date();
  @Input() set endDate(val: Date) {
    this._endDate = val;
  }

  get endDate() {
    return this._endDate;
  }

  _startDate = new Date(
    this.endDate.getFullYear(),
    this.endDate.getMonth() - 3,
    1
  );
  @Input() set startDate(val: Date) {
    this._startDate = val;
  }

  get startDate() {
    return this._startDate;
  }

  ngOnInit() {
    this.initForms(); // Move this to the top

    this.merchantAccountInfoSub = this.merchantAccountInfoSet.subscribe(
      () => {
        if (this.merchantAccountInfo) {
          this.stripAccount = this.merchantAccountInfo.customerAccounts[0].account;

          // Call getCardLimit here, after stripAccount is set
          this.merchantService
            .getCardLimit(this.stripAccount)
            .subscribe(
              (data: any) => {
                console.log('Card limit response:', data);
                this.cardLimit = Number(data.cardLimit).toFixed(2);
                this.merchantCardLimitsForm.patchValue(
                  { cardLimit: this.cardLimit },
                  { onlySelf: true }
                );
              },
              (error) => {
                console.error('Card limit error:', error);
                this.messageService.error('Could not load Card Limit');
              }
            );
        }
      },
      (error) => {
        this.messageService.error('Error in retrieving merchant account info');
      }
    );

    this.merchantService.loadAdjustments().subscribe(
      (data: AdjustmentReason[]) => {
        this.adjustmentReasons = data;
        this.adjustReason = this.adjustmentReasons[0].COLUMN_VALUE;
        this.financialAdjustmentsForm.patchValue(
          { adjustReason: this.adjustReason },
          { onlySelf: true }
        );
      },
      () => {
        this.messageService.error('Could not load Adjustment Reasons');
      }
    );

    this.merchantService
      .getMerchantData(this.merchant.ncMerchantNum, this.merchant.location)
      .subscribe(
        (data: object) => {
          this.srcAccts = data['customerAccounts'];
          this.srcAcct = this.srcAccts[0].account;
          this.balanceTransferForm.patchValue(
            { srcAcct: this.srcAcct },
            { onlySelf: true }
          );
        },
        () => {
          this.messageService.error('Could not Load Accounts');
        }
      );
  }

  initForms() {
    this.financialAdjustmentsForm = new UntypedFormGroup({
      adjustReason: new UntypedFormControl(this.adjustReason),
      adjustAmt: new UntypedFormControl(this.adjustAmt, [
        customValidator.moneyValidator,
        Validators.pattern(/^(\d{1,8}\.\d{1,2}|\d+)$/)
      ])
    });
    this.balanceTransferForm = new UntypedFormGroup({
      srcAcct: new UntypedFormControl(this.srcAcct),
      desAcct: new UntypedFormControl(this.desAcct, [
        customValidator.accountValidator,
        Validators.pattern(/^[0-9]{3,16}$/)
      ]),
      xferAmt: new UntypedFormControl(this.xferAmt, [
        customValidator.moneyValidator,
        Validators.pattern(/^(\d{1,8}\.\d{1,2}|\d+)$/)
      ])
    });
    this.merchantCardLimitsForm = new UntypedFormGroup({
      cardLimit: new UntypedFormControl(this.cardLimit, [
        Validators.required,
        customValidator.moneyValidator,
        Validators.pattern(/^(\d{1,8}\.\d{1,2}|\d+)$/),
        Validators.maxLength(11)
      ])
    });
  }

  cardLimitUpdateClicked() {
    // Validation helper
    Object.keys(this.merchantCardLimitsForm.controls).forEach(field => {
      const control = this.merchantCardLimitsForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  updateCardLimit() {
    const submittedLimit = this.merchantCardLimitsForm.value['cardLimit'];

    this.merchantService.updateCardLimit(
      this.stripAccount,
      submittedLimit
    ).subscribe(
      () => {
        this.messageService.success('Card Limit Updated Successfully.');
        this.cardLimit = submittedLimit; // Update the component property
        // Remove the patchValue that clears the field
        this.merchantCardLimitsForm.controls['cardLimit'].markAsUntouched();
      },
      error => {
        if (error.error) {
          this.messageService.error(error.error);
        } else {
          this.messageService.error('Card Limit Update Failed');
        }
      }
    );
  }
  get cardLimitFormHandle() {
    return this.merchantCardLimitsForm.controls;
  }


  balanceTransferClicked() {
    // Validation helper
    Object.keys(this.balanceTransferForm.controls).forEach(field => {
      const control = this.balanceTransferForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  adjustmentTransferClicked() {
    // Validation helper
    Object.keys(this.financialAdjustmentsForm.controls).forEach(field => {
      const control = this.financialAdjustmentsForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  applyAdjustment() {
    const params = {
      accountNum: this.merchantAccountInfo.merchant.account,
      adjustReason: this.financialAdjustmentsForm.value['adjustReason'],
      adjustAmt: this.financialAdjustmentsForm.value['adjustAmt']
    };
    this.merchantService.applyAdjustment(params).subscribe(
      () => {
        this.messageService.success('Balance Adjusted Successfully.');
        (<UntypedFormGroup>this.financialAdjustmentsForm).patchValue(
          { adjustAmt: '' },
          { onlySelf: true }
        );
        this.financialAdjustmentsForm.controls[
          'adjustAmt'
          ].markAsUntouched();
        (<UntypedFormGroup>this.financialAdjustmentsForm).patchValue(
          { adjustReason: this.adjustmentReasons[0].COLUMN_VALUE },
          { onlySelf: true }
        );
        this.transferSummary = null;
      },
      error => {
        if (error.error) {
          this.messageService.error(error.error);
        } else {
          this.messageService.error('Transfer Failed');
        }
      }
    );
  }

  balanceTransfer() {
    const test = this.balanceTransferForm.valid;
    const params = {
      srcAcct: this.balanceTransferForm.value['srcAcct'],
      desAcct: this.balanceTransferForm.value['desAcct'],
      xferAmt: this.balanceTransferForm.value['xferAmt']
    };
    this.merchantService.balanceTransfer(params).subscribe(
      data => {
        this.messageService.success('Balance Transfered Successfully.');
        (<UntypedFormGroup>this.balanceTransferForm).patchValue(
          { srcAcct: this.srcAccts[0].account },
          { onlySelf: true }
        );
        (<UntypedFormGroup>this.balanceTransferForm).patchValue(
          { desAcct: '' },
          { onlySelf: true }
        );
        (<UntypedFormGroup>this.balanceTransferForm).patchValue(
          { xferAmt: '' },
          { onlySelf: true }
        );
        this.transferSummary = JSON.parse(data);
      },
      data => {
        this.messageService.error('Transfer Failed ' + data.error);
      }
    );
  }

  get finAdjFormHandle() {
    return this.financialAdjustmentsForm.controls;
  }
  get balTransFormHandle() {
    return this.balanceTransferForm.controls;
  }

  hasEntitlement(entitlement: string) {
    return this.entitlementService.hasEntitlement(entitlement);
  }

  reverse(transaction: any) {
    const that = this;
    this.cardHolderService.reverse(transaction.tranSequence).subscribe(
      () => {
        transaction.reverseState = -1;
        this.messageService.success('Reversal Successful');
      },
      error => {
        that.messageService.error(error.error);
      }
    );
  }

  loadTransactionHistory() {
    if (this.srcAcct === this.srcAccts[0].account) {
      this.merchantService
        .getTransHistory(
          this.startDate,
          this.endDate,
          this.srcAccts[0].account,
          'payroll'
        )
        .subscribe(data => {
          this.transactions = data['transactions'];
          this.accountType = 'Strip';
        });
    } else {
      this.merchantService
        .getTransHistory(this.startDate, this.endDate, this.srcAcct, 'chip')
        .subscribe(data => {
          this.transactions = data['transactions'];
          this.accountType = 'Chip';
        });
    }
  }
  showDetails(expandedTrans: object) {
    expandedTrans['c2cDetails'] = '';

    if (
      expandedTrans['type'] === '0061' ||
      expandedTrans['type'] === '0260' ||
      expandedTrans['type'] === '0331' ||
      expandedTrans['type'] === '0334'
    ) {
      this.cardHolderService
        .getC2CDetails(
          this.merchant.ncMerchantNum,
          expandedTrans['tranSequence'],
          expandedTrans['type']
        )
        .subscribe(
          data => {
            expandedTrans['c2cDetails'] = data.typeDescription;
          },
          err => {
            expandedTrans['c2cDetails'] = '';
            console.log(err);
          }
        );
      return;
    }
    if (
      expandedTrans['type'] === '0261' &&
      expandedTrans['srcTermNum'].indexOf('C2S') > -1
    ) {
      expandedTrans['c2cDetails'] = 'Chip to Chip Correction - Strip Credit';
      return;
    }
  }
}
